"use client";

import { TScreenSizes } from "@/app/types/types";
import { createContext, useEffect, useState } from "react";

const IPAD_QUERY = "(max-width: 1024px)";
const MOBILE_QUERY = "(max-width: 767px)";

export const screenSizeContext = createContext<TScreenSizes>({
	isIpad: false,
	isMobile: false,
	isReady: false,
});

/**
 * Viewport flags stay `false` on the server and first client paint so SSR HTML
 * matches hydration (avoids React #418). After mount, matchMedia updates them
 * together with `isReady` so scroll setup never runs on a stale desktop default.
 */
export function ScreenSizeProvider({ children }: { children: React.ReactNode }) {
	const [screen, setScreen] = useState<TScreenSizes>({
		isIpad: false,
		isMobile: false,
		isReady: false,
	});

	useEffect(() => {
		const ipadMq = window.matchMedia(IPAD_QUERY);
		const mobileMq = window.matchMedia(MOBILE_QUERY);

		const sync = () => {
			setScreen({
				isIpad: ipadMq.matches,
				isMobile: mobileMq.matches,
				isReady: true,
			});
		};

		sync();
		ipadMq.addEventListener("change", sync);
		mobileMq.addEventListener("change", sync);
		return () => {
			ipadMq.removeEventListener("change", sync);
			mobileMq.removeEventListener("change", sync);
		};
	}, []);

	return (
		<screenSizeContext.Provider value={screen}>
			{children}
		</screenSizeContext.Provider>
	);
}
